import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, Eye, MousePointer, Target } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientSearch({ client, getHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/client/dashboard/search?days=${period}`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders, period]);

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">Loading search analytics...</div>;
  if (!data) return <div className="text-red-400 text-center py-12">Failed to load analytics</div>;

  const maxImpressions = Math.max(...(data.daily_data.map(d => d.impressions) || [1]));

  return (
    <div className="space-y-6" data-testid="search-tab">
      {/* Period selector */}
      <div className="flex gap-2">
        {[7, 14, 30, 90].map(d => (
          <button
            key={d}
            onClick={() => setPeriod(d)}
            className={`px-3 py-1.5 rounded-lg text-xs transition-all ${period === d ? 'bg-blue-600 text-gray-900' : 'bg-gray-800 text-gray-400 hover:text-gray-900'}`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5">
            <p className="text-xs text-gray-500 uppercase">Impressions</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{data.total_impressions.toLocaleString()}</p>
            <div className="flex items-center gap-1 mt-1 text-xs text-cyan-400">
              <Eye className="h-3 w-3" /> Search appearances
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5">
            <p className="text-xs text-gray-500 uppercase">Clicks</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{data.total_clicks.toLocaleString()}</p>
            <div className="flex items-center gap-1 mt-1 text-xs text-blue-400">
              <MousePointer className="h-3 w-3" /> Website visits
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5">
            <p className="text-xs text-gray-500 uppercase">CTR</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{data.ctr}%</p>
            <div className="flex items-center gap-1 mt-1 text-xs text-green-400">
              <TrendingUp className="h-3 w-3" /> Click-through rate
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5">
            <p className="text-xs text-gray-500 uppercase">Avg Position</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{data.avg_position}</p>
            <div className="flex items-center gap-1 mt-1 text-xs text-amber-400">
              <Target className="h-3 w-3" /> Search ranking
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-gray-900 text-base">Search Impressions ({period} days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-[2px] h-40">
            {data.daily_data.slice().reverse().map((d, i) => {
              const height = maxImpressions > 0 ? (d.impressions / maxImpressions * 100) : 0;
              return (
                <div key={d.date || `day-${i}`} className="flex-1 group relative" title={`${d.date?.split('T')[0] || ''}: ${d.impressions} impressions, ${d.clicks} clicks`}>
                  <div
                    className="w-full rounded-t bg-blue-500/80 hover:bg-blue-400 transition-all cursor-pointer"
                    style={{height: `${Math.max(height, 2)}%`}}
                  />
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                    <div className="bg-gray-900 border border-gray-200 rounded px-2 py-1 text-[10px] text-gray-900 whitespace-nowrap">
                      {d.impressions} imp / {d.clicks} clicks
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-gray-500">
            <span>{data.daily_data.length > 0 ? data.daily_data[data.daily_data.length - 1]?.date?.split('T')[0] : ''}</span>
            <span>{data.daily_data.length > 0 ? data.daily_data[0]?.date?.split('T')[0] : ''}</span>
          </div>
        </CardContent>
      </Card>

      {/* Top Queries */}
      {data.daily_data.length > 0 && data.daily_data[0].top_queries && (
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-gray-900 text-base">Top Search Queries</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.daily_data[0].top_queries.map((q) => (
                <div key={q.query} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                  <span className="text-sm text-gray-300">{q.query}</span>
                  <Badge className="bg-gray-800 text-gray-400 text-xs">{q.impressions} imp</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
